import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../utils/AuthContext';
import {
  uploadProjectFile,
  uploadProjectFinancialFile,
  uploadProjectFileVersion,
  uploadSharedWholeProjectVersion,
  copyProjectFileOntoStream,
  createProjectFileStream,
  subscribeProjectFileStreams,
  listCncProjectFiles,
  normalizeKindTagForFile,
  peekCsvText,
} from '../../utils/projectFileService';
import { doc, collection } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { subscribeProposalStructures, type StructureRef } from '../../utils/proposalStructureApi';
import { buildHubStructureOptions } from '../../utils/projectHubStructureOptions';
import {
  placementsForUploadGroups,
  projectIsSplitIntoStructures,
  structureTrackKindLabel,
} from '../../utils/projectFileStructureTracks';
import { uploadStructureGroups } from '../../utils/projectFileStreamGroups';
import { structureSelectionLabel } from '../../utils/structureSelectionLabel';
import { STRUCTURES_LOCKED_HINT, structuresUnlocked } from '../../utils/projectStructureGate';
import {
  claimAfterDroppingStructures,
  cncStructureConflicts,
  type CncModelClaim,
} from '../../utils/cncModelStructures';
import type { ProjectFile, ProjectFileStream, ProjectFileStreamKind } from '../../types';
import ProjectUploadModalDialog from './ProjectUploadModalDialog';
import { isFinancialsFolderPath } from '../../utils/projectFileFolders';
import {
  emptyGeneralUploadRow,
  emptyQuoteUploadRow,
  emptyVersionUploadRowUnified,
  generalRowFromFile,
  quoteRowFromFile,
  unifiedRowFromFile,
  dispositionsForVersionUploadRows,
  pickMatchingStreamIdForUpload,
  versionRowStreamKind,
  type ProjectUploadModalKind,
  type ProjectUploadRow,
} from './projectUploadModalShared';

export type ProjectUploadSuccessPayload = {
  files: ProjectFile[];
  lastVersionStreamId: string | null;
};

export type ProjectUploadModalOpenOptions = {
  initialFiles?: File[];
  /** Defaults for new rows (e.g. Files tab selected track). */
  preferredStreamId?: string | null;
  /** Default project folder for general rows (Project folders view). null/undefined = Unsorted. */
  preferredFolderPath?: string | null;
  /** Open in "General document" mode when no files are pre-attached. */
  preferGeneral?: boolean;
  /** Upload to this project when set (e.g. task comments while hub shows “all projects”). */
  projectIdOverride?: string;
  /** Deal id for timber-list sync etc.; defaults to hub deal when omitted. */
  dealIdOverride?: string;
  onSuccess?: (result: ProjectUploadSuccessPayload) => void;
};

export type ProjectUploadModalContextValue = {
  openProjectUploadModal: (opts?: ProjectUploadModalOpenOptions) => void;
  uploading: boolean;
  uploadProgress: number;
};

const noopContext: ProjectUploadModalContextValue = {
  openProjectUploadModal: () => {},
  uploading: false,
  uploadProgress: 0,
};

const ProjectUploadModalContext = createContext<ProjectUploadModalContextValue>(noopContext);

export function useProjectUploadModal(): ProjectUploadModalContextValue {
  return useContext(ProjectUploadModalContext);
}

/** Hub-mounted upload modal; outside the provider, `open` is a no-op. */
export function useOptionalProjectUploadModal(): ProjectUploadModalContextValue {
  return useContext(ProjectUploadModalContext);
}

type ProviderProps = {
  /** Hub’s selected project (Files tab / header context). */
  projectId: string | null;
  dealId?: string;
  children: React.ReactNode;
};

export function ProjectUploadModalProvider({ projectId, dealId, children }: ProviderProps) {
  const { user } = useAuth();
  const [streams, setStreams] = useState<ProjectFileStream[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadTargetProjectId, setUploadTargetProjectId] = useState<string | null>(null);
  const [uploadRows, setUploadRows] = useState<ProjectUploadRow[]>([]);
  /** Top-level modal mode: version tracks vs general project files (drives row shape and file picker behavior). */
  const [uploadModalKind, setUploadModalKind] = useState<ProjectUploadModalKind>('version');
  const [proposalStructures, setProposalStructures] = useState<StructureRef[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const allStreamsRef = useRef<ProjectFileStream[]>([]);
  const hintStreamIdRef = useRef<string | null>(null);
  const hintFolderPathRef = useRef<string>('');
  const dealForUploadRef = useRef<string | undefined>(undefined);
  const pendingSuccessRef = useRef<((result: ProjectUploadSuccessPayload) => void) | undefined>(undefined);
  const uploadRowsRef = useRef<ProjectUploadRow[]>([]);
  uploadRowsRef.current = uploadRows;

  useEffect(() => {
    if (!uploadModalOpen || !uploadTargetProjectId) {
      setStreams([]);
      allStreamsRef.current = [];
      return;
    }
    // Whole-project roots stay out of the track picker. Whole project uploads
    // still land on that one shared track so every structure opens the same file.
    return subscribeProjectFileStreams(uploadTargetProjectId, list => {
      allStreamsRef.current = list;
      setStreams(list.filter(s => !s.wholeProjectRoot));
    });
  }, [uploadModalOpen, uploadTargetProjectId]);

  useEffect(() => {
    const deal = dealForUploadRef.current ?? dealId;
    if (!uploadModalOpen || !deal) {
      setProposalStructures([]);
      return;
    }
    return subscribeProposalStructures(deal, setProposalStructures, () => setProposalStructures([]));
  }, [uploadModalOpen, dealId]);

  /** Id-keyed options with current names (proposal preferred; stream tags for designers). */
  const hubStructures = useMemo(
    () => buildHubStructureOptions(proposalStructures, streams),
    [proposalStructures, streams],
  );

  // Structure breakdown only exists once the first timber list is on the project
  // (the upload modal only sees version tracks, which is where timber lists live).
  const structuresReady = useMemo(
    () => structuresUnlocked({ streams, structures: hubStructures }),
    [streams, hubStructures],
  );

  const endSession = useCallback(() => {
    pendingSuccessRef.current = undefined;
    hintStreamIdRef.current = null;
    hintFolderPathRef.current = '';
    dealForUploadRef.current = undefined;
    setUploadTargetProjectId(null);
    setUploadModalOpen(false);
    setUploadRows([]);
    setUploadModalKind('version');
  }, []);

  const closeModal = useCallback(() => {
    endSession();
  }, [endSession]);

  const appendUploadFiles = useCallback(
    (list: File[]) => {
      if (list.length === 0) return;
      const hint = hintStreamIdRef.current;

      if (uploadModalKind === 'general') {
        const folder = hintFolderPathRef.current;
        void Promise.all(list.map(async f => generalRowFromFile(f, await peekCsvText(f), folder))).then(added => {
          setUploadRows(prev => {
            const kept = prev.filter(r => r.file);
            return [...kept, ...added];
          });
        });
        return;
      }

      if (uploadModalKind === 'quote') {
        setUploadRows(prev => {
          const kept = prev.filter(r => r.file);
          return [...kept, ...list.map(f => quoteRowFromFile(f))];
        });
        return;
      }

      // Version tab: classify like openProjectUploadModal so general-only drops (e.g. .docx) are not added as hidden rows.
      const folderHint = hintFolderPathRef.current;
      void Promise.all(
        list.map(async f => unifiedRowFromFile(f, streams, hint, await peekCsvText(f), folderHint)),
      ).then(mapped => {
      const versionRows = mapped.filter((r): r is ProjectUploadRow & { rowKind: 'version' } => r.rowKind === 'version');
      const generalRows = mapped.filter((r): r is ProjectUploadRow & { rowKind: 'general' } => r.rowKind === 'general');

      if (versionRows.length > 0 && generalRows.length > 0) {
        window.alert(
          'Mix of version files (PDF, HTML, 3D, CSV) and general files in one drop. Adding version files first — switch to “General document” and add the rest in a second batch.',
        );
        setUploadModalKind('version');
        setUploadRows(prev => {
          const kept = prev.filter(r => r.file);
          return [...kept, ...versionRows];
        });
        return;
      }

      if (generalRows.length > 0 && versionRows.length === 0) {
        const kept = uploadRowsRef.current.filter(r => r.file);
        const versionKept = kept.filter(r => r.rowKind === 'version');
        if (versionKept.length > 0) {
          const ok = window.confirm(
            'These files upload as general documents. Staged version-control files will be removed. Continue?',
          );
          if (!ok) return;
        }
        setUploadModalKind('general');
        setUploadRows([...kept.filter(r => r.rowKind === 'general'), ...generalRows]);
        return;
      }

      setUploadRows(prev => {
        const kept = prev.filter(r => r.file);
        return [...kept, ...mapped];
      });
      });
    },
    [streams, uploadModalKind],
  );

  const changeUploadModalKind = useCallback(
    (next: ProjectUploadModalKind) => {
      if (next === uploadModalKind) return;
      if (
        uploadRows.some(r => r.file) &&
        !window.confirm('Switching upload type will clear all staged files and rows. Continue?')
      ) {
        return;
      }
      setUploadModalKind(next);
      setUploadRows(
        next === 'version'
          ? [emptyVersionUploadRowUnified(streams, hintStreamIdRef.current)]
          : next === 'quote'
            ? [emptyQuoteUploadRow()]
            : [emptyGeneralUploadRow(hintFolderPathRef.current)],
      );
    },
    [uploadModalKind, uploadRows, streams],
  );

  const openProjectUploadModal = useCallback(
    (opts?: ProjectUploadModalOpenOptions) => {
      if (!user?.id) return;
      const targetPid = opts?.projectIdOverride ?? projectId;
      if (!targetPid) return;
      const targetDeal = opts?.dealIdOverride ?? dealId;
      dealForUploadRef.current = targetDeal;
      const pref = opts?.preferredStreamId ?? null;
      hintStreamIdRef.current = pref;
      const folder = opts?.preferredFolderPath ?? '';
      hintFolderPathRef.current = folder;
      pendingSuccessRef.current = opts?.onSuccess;
      setUploadTargetProjectId(targetPid);
      if (opts?.initialFiles && opts.initialFiles.length > 0) {
        void Promise.all(
          opts.initialFiles.map(async f =>
            opts.preferGeneral
              ? generalRowFromFile(f, await peekCsvText(f), folder)
              : unifiedRowFromFile(f, streams, pref, await peekCsvText(f), folder),
          ),
        ).then(rows => {
          const versionRows = rows.filter(r => r.rowKind === 'version');
          const generalRows = rows.filter(r => r.rowKind === 'general');
          if (versionRows.length > 0 && generalRows.length > 0) {
            window.alert(
              'Mix of version files (PDF, HTML, 3D, CSV) and general files in one drop. Uploading version files first — switch to “General document” and add photos or other files in a second batch.',
            );
            setUploadRows(versionRows);
            setUploadModalKind('version');
          } else if (versionRows.length > 0) {
            setUploadRows(rows);
            setUploadModalKind('version');
          } else {
            setUploadRows(rows);
            setUploadModalKind('general');
          }
        });
      } else if (opts?.preferGeneral) {
        setUploadRows([emptyGeneralUploadRow(folder)]);
        setUploadModalKind('general');
      } else {
        setUploadRows([emptyVersionUploadRowUnified(streams, pref)]);
        setUploadModalKind('version');
      }
      setUploadModalOpen(true);
    },
    [projectId, dealId, user?.id, streams],
  );

  const submitProjectUpload = useCallback(async () => {
    if (!user?.id) return;
    const pid = uploadTargetProjectId;
    if (!pid) return;
    const effectiveDeal = dealForUploadRef.current;
    const rows = uploadRows.filter(r => r.rowKind === uploadModalKind);
    if (rows.length === 0) return;

    const newTrackLeaderValidated = new Set<ProjectFileStreamKind>();
    const dispositions = dispositionsForVersionUploadRows(uploadRows, streams);

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.file) {
        alert('Choose a file for every row, or remove empty rows.');
        return;
      }
      const file = row.file;
      if (row.rowKind === 'general' || row.rowKind === 'quote') continue;

      const vt = row.versionLabel.trim();
      if (!vt) {
        alert(`Enter a version label for “${file.name}”.`);
        return;
      }
      if (!row.detailStage) {
        alert(`Choose an LOD for “${file.name}”.`);
        return;
      }
      const disposition = dispositions.get(row.clientId) ?? { kind: 'empty' as const };
      if (versionRowStreamKind(row, disposition) === 'three_d_raw' && row.forCnc) {
        if (!structuresReady) {
          alert(`“${file.name}” cannot be marked For CNC yet — ${STRUCTURES_LOCKED_HINT}`);
          return;
        }
        if (row.cncStructureIds.length === 0) {
          alert(`Choose which structures “${file.name}” is for.`);
          return;
        }
      }

      const matching = streams.filter(s => s.streamKind === row.newKind);

      if (matching.length > 1) {
        const okPick = matching.some(s => s.id === row.pickStreamId);
        if (!okPick) {
          alert(`Choose which history to use for “${file.name}”.`);
          return;
        }
      }

      if (matching.length === 0) {
        const routingKind = row.newKind;
        if (newTrackLeaderValidated.has(routingKind)) continue;
        if (!row.newTitle.trim()) {
          alert(`Enter a title for the new version history (“${file.name}”).`);
          return;
        }
        newTrackLeaderValidated.add(routingKind);
      }
    }

    const cncPlanByClientId = new Map<string, {
      structureIds: string[];
      structureNames: string[];
      releaseFrom: { fileId: string; structureIds: string[] }[];
    }>();
    if (uploadModalKind === 'version') {
      let claims: CncModelClaim[] = [];
      try {
        claims = (await listCncProjectFiles(pid))
          .filter((file) => file.forCnc && file.id)
          .map((file) => ({
            fileId: file.id!,
            fileName: file.name,
            structureIds: file.cncStructureIds ?? [],
            structureNames: file.cncStructureNames ?? [],
          }));
      } catch (err) {
        console.error(err);
        alert('Could not check which structures already have a CNC model.');
        return;
      }
      for (const row of rows) {
        if (row.rowKind !== 'version' || !row.forCnc || !row.file) continue;
        const disposition = dispositions.get(row.clientId) ?? { kind: 'empty' as const };
        if (versionRowStreamKind(row, disposition) !== 'three_d_raw') continue;
        const structureIds = row.cncStructureIds;
        const structureNames = structureIds.map(
          (id) => hubStructures.find((structure) => structure.id === id)?.name || id,
        );
        const conflicts = cncStructureConflicts(claims, structureIds, structureNames);
        if (conflicts.length > 0) {
          const lines = conflicts.map(
            (conflict) => `${conflict.structureName} is already on “${conflict.fileName}”.`,
          );
          const ok = window.confirm(
            `${lines.join('\n')}\n\nUploading “${row.file.name}” will move those structures onto this file.`,
          );
          if (!ok) return;
        }
        const byFile = new Map<string, string[]>();
        for (const conflict of conflicts) {
          const list = byFile.get(conflict.fileId) ?? [];
          list.push(conflict.structureId);
          byFile.set(conflict.fileId, list);
        }
        cncPlanByClientId.set(row.clientId, {
          structureIds,
          structureNames,
          releaseFrom: Array.from(byFile.entries()).map(([fileId, ids]) => ({ fileId, structureIds: ids })),
        });
        claims = claims.flatMap((claim) => {
          const drop = byFile.get(claim.fileId);
          return drop ? [claimAfterDroppingStructures(claim, drop)] : [claim];
        }).filter((claim) => claim.structureIds.length > 0);
        claims.push({
          fileId: `pending:${row.clientId}`,
          fileName: row.file.name,
          structureIds,
          structureNames,
        });
      }
    }

    setUploading(true);
    setUploadProgress(0);
    const createdFiles: ProjectFile[] = [];
    try {
      let lastSid: string | null = null;
      const total = rows.length;
      let done = 0;
      const prefHint = hintStreamIdRef.current;
      const batchStreamByRoutingKind = new Map<ProjectFileStreamKind, string>();
      const pendingCncFileIds = new Map<string, string>();
      const projectSplit = projectIsSplitIntoStructures(streams) && hubStructures.length > 0;
      let liveStreams = streams;

      for (const row of rows) {
        if (row.rowKind === 'general') {
          const normalized = normalizeKindTagForFile(row.file!, row.kindTag);
          const pf = isFinancialsFolderPath(row.folderPath)
            ? await uploadProjectFinancialFile(
              pid,
              row.file!,
              user.id,
              user.name || user.email || 'Unknown',
            )
            : await uploadProjectFile(pid, row.file!, user.id, user.name || user.email || 'Unknown', {
              kindTag: normalized,
              dealId: effectiveDeal,
              folderPath: row.folderPath || undefined,
            });
          createdFiles.push(pf);
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
          continue;
        }

        if (row.rowKind === 'quote') {
          const pf = await uploadProjectFile(pid, row.file!, user.id, user.name || user.email || 'Unknown', {
            fileSection: 'quote',
            quoteType: row.quoteType,
            dealId: effectiveDeal,
          });
          createdFiles.push(pf);
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
          continue;
        }

        const file = row.file!;
        const matching = liveStreams.filter(s => s.streamKind === row.newKind);

        if (projectSplit && row.newStructureIds.length === 0) {
          const detailStage = row.detailStage;
          if (!detailStage) {
            alert(`Choose an LOD for “${file.name}”.`);
            return;
          }
          const cncPlan = cncPlanByClientId.get(row.clientId);
          const releaseFrom = cncPlan
            ? cncPlan.releaseFrom.flatMap((release) => {
                const releasedId = release.fileId.startsWith('pending:')
                  ? pendingCncFileIds.get(release.fileId)
                  : release.fileId;
                return releasedId ? [{ fileId: releasedId, structureIds: release.structureIds }] : [];
              })
            : [];
          try {
            const result = await uploadSharedWholeProjectVersion(
              pid,
              file,
              user.id,
              user.name || user.email || 'Unknown',
              row.versionLabel.trim(),
              row.newKind,
              allStreamsRef.current,
              {
                versionNote: row.versionNote.trim(),
                dealId: effectiveDeal,
                detailStage,
                ...(row.newKind === 'timber_list_csv' && row.trueDimensions ? { trueDimensions: true } : {}),
                ...(cncPlan
                  ? {
                      forCnc: true,
                      cncStructureIds: cncPlan.structureIds,
                      cncStructureNames: cncPlan.structureNames,
                      ...(releaseFrom.length > 0 ? { cncReleaseFrom: releaseFrom } : {}),
                    }
                  : {}),
              },
            );
            allStreamsRef.current = result.streams;
            if (result.file.id) pendingCncFileIds.set(`pending:${row.clientId}`, result.file.id);
            createdFiles.push(result.file);
          } catch (err) {
            console.error(err);
            alert(`Could not upload “${file.name}” as a whole-project file.`);
            return;
          }
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
          continue;
        }

        if (projectSplit) {
          const wanted = row.newStructureIds.length > 0
            ? hubStructures.filter(s => row.newStructureIds.includes(s.id))
            : hubStructures;
          if (wanted.length === 0) {
            alert(`Choose a structure group for “${file.name}”, or leave Whole project selected.`);
            return;
          }
          const uploadGroups = uploadStructureGroups(liveStreams, hubStructures);
          const groups = placementsForUploadGroups(
            liveStreams,
            row.newKind,
            wanted.map(s => s.id),
            uploadGroups,
          );
          const destinations: { streamId: string; structureIds: string[] }[] = [];
          for (const group of groups) {
            if (group.streamId) {
              const track = liveStreams.find(s => s.id === group.streamId);
              destinations.push({
                streamId: group.streamId,
                structureIds: track?.structureIds?.length ? track.structureIds : group.structureIds,
              });
              continue;
            }
            const refs = group.structureIds
              .map(id => hubStructures.find(s => s.id === id))
              .filter((s): s is StructureRef => !!s);
            if (refs.length === 0) continue;
            const kindLabel = row.newTitle.trim() || structureTrackKindLabel(row.newKind);
            const title = group.groupLabel
              ? `${group.groupLabel} — ${kindLabel}`
              : `${structureSelectionLabel(refs.map(s => s.name), { maxNamed: 3 })} — ${kindLabel}`;
            try {
              const created = await createProjectFileStream(
                pid,
                title,
                row.newKind,
                user.id,
                undefined,
                {
                  structures: refs,
                  ...(group.groupLabel ? { structureGroupLabel: group.groupLabel } : {}),
                },
              );
              if (!created.id) throw new Error('Missing track id');
              liveStreams = [...liveStreams, created];
              destinations.push({ streamId: created.id, structureIds: refs.map(s => s.id) });
            } catch (err) {
              console.error(err);
              alert(`Could not create a ${structureTrackKindLabel(row.newKind)} track for “${file.name}”.`);
              return;
            }
          }
          if (destinations.length === 0) {
            alert(`Missing version history for “${file.name}”.`);
            return;
          }
          const detailStage = row.detailStage;
          if (!detailStage) {
            alert(`Choose an LOD for “${file.name}”.`);
            return;
          }
          const cncPlan = cncPlanByClientId.get(row.clientId);
          // One real upload, then quiet linked copies onto the other structure
          // tracks. Re-uploading the same bytes per destination used to fan out
          // N "new file version" bells for a single user action (CRM #82).
          const primary = destinations[0];
          const primaryCncIds = cncPlan
            ? cncPlan.structureIds.filter(id => primary.structureIds.includes(id))
            : [];
          const releaseFrom = cncPlan && primaryCncIds.length > 0
            ? cncPlan.releaseFrom.flatMap((release) => {
                const releasedId = release.fileId.startsWith('pending:')
                  ? pendingCncFileIds.get(release.fileId)
                  : release.fileId;
                return releasedId ? [{ fileId: releasedId, structureIds: release.structureIds }] : [];
              })
            : [];
          const structureReleaseId = doc(collection(db, 'projectFiles')).id;
          const actor = {
            uid: user.id,
            name: user.name || user.email || 'Unknown',
          };
          const pf = await uploadProjectFileVersion(
            pid,
            primary.streamId,
            file,
            user.id,
            actor.name,
            row.versionLabel.trim(),
            {
              versionNote: row.versionNote.trim(),
              dealId: effectiveDeal,
              detailStage,
              structureReleaseId,
              ...(row.newKind === 'timber_list_csv' && row.trueDimensions ? { trueDimensions: true } : {}),
              ...(cncPlan && primaryCncIds.length > 0
                ? {
                    forCnc: true,
                    cncStructureIds: primaryCncIds,
                    cncStructureNames: primaryCncIds.map(
                      id => cncPlan.structureNames[cncPlan.structureIds.indexOf(id)] || id,
                    ),
                    ...(releaseFrom.length > 0 ? { cncReleaseFrom: releaseFrom } : {}),
                  }
                : {}),
            },
          );
          if (!pf.id) {
            alert(`Upload of “${file.name}” did not return a file id.`);
            return;
          }
          pendingCncFileIds.set(`pending:${row.clientId}`, pf.id);
          createdFiles.push(pf);
          lastSid = primary.streamId;
          for (const destination of destinations.slice(1)) {
            await copyProjectFileOntoStream(pid, pf.id, destination.streamId, {
              structureReleaseId,
              uploadedAsWholeProject: false,
              actor,
            });
            lastSid = destination.streamId;
          }
          done += 1;
          setUploadProgress(Math.round((done / total) * 100));
          continue;
        }

        let sid = '';

        if (matching.length > 0) {
          sid = pickMatchingStreamIdForUpload(matching, prefHint, row.pickStreamId);
        } else {
          const routingKind = row.newKind;
          const fromBatch = batchStreamByRoutingKind.get(routingKind);
          if (fromBatch) {
            sid = fromBatch;
          } else {
            try {
              const structures = row.newStructureIds
                .map(id => hubStructures.find(s => s.id === id))
                .filter((s): s is StructureRef => !!s);
              const created = await createProjectFileStream(
                pid,
                row.newTitle.trim(),
                row.newKind,
                user.id,
                undefined,
                structures.length > 0 ? { structures } : undefined,
              );
              sid = created.id || '';
              if (sid) batchStreamByRoutingKind.set(routingKind, sid);
            } catch (err) {
              console.error(err);
              alert(`Could not create version history for “${file.name}”.`);
              return;
            }
          }
        }

        if (!sid) {
          alert(`Missing version history for “${file.name}”.`);
          return;
        }

        const detailStage = row.detailStage;
        if (!detailStage) {
          alert(`Choose an LOD for “${file.name}”.`);
          return;
        }

        const cncPlan = cncPlanByClientId.get(row.clientId);
        const pf = await uploadProjectFileVersion(
          pid,
          sid,
          file,
          user.id,
          user.name || user.email || 'Unknown',
          row.versionLabel.trim(),
          {
            versionNote: row.versionNote.trim(),
            dealId: effectiveDeal,
            detailStage,
            ...(row.newKind === 'timber_list_csv' && row.trueDimensions ? { trueDimensions: true } : {}),
            ...(cncPlan
              ? {
                  forCnc: true,
                  cncStructureIds: cncPlan.structureIds,
                  cncStructureNames: cncPlan.structureNames,
                  cncReleaseFrom: cncPlan.releaseFrom.flatMap((release) => {
                    const fileId = release.fileId.startsWith('pending:')
                      ? pendingCncFileIds.get(release.fileId)
                      : release.fileId;
                    return fileId ? [{ fileId, structureIds: release.structureIds }] : [];
                  }),
                }
              : {}),
          },
        );
        if (pf.id) pendingCncFileIds.set(`pending:${row.clientId}`, pf.id);
        createdFiles.push(pf);
        lastSid = sid;
        done += 1;
        setUploadProgress(Math.round((done / total) * 100));
      }

      const onSuccess = pendingSuccessRef.current;
      pendingSuccessRef.current = undefined;
      hintStreamIdRef.current = null;
      dealForUploadRef.current = undefined;
      onSuccess?.({ files: createdFiles, lastVersionStreamId: lastSid });
      setUploadTargetProjectId(null);
      setUploadModalOpen(false);
      setUploadRows([]);
      setUploadModalKind('version');
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [user, uploadRows, streams, uploadTargetProjectId, uploadModalKind, hubStructures, structuresReady]);

  const contextValue = useMemo<ProjectUploadModalContextValue>(
    () => ({
      openProjectUploadModal,
      uploading,
      uploadProgress,
    }),
    [openProjectUploadModal, uploading, uploadProgress],
  );

  const hintStreamId = hintStreamIdRef.current;

  return (
    <ProjectUploadModalContext.Provider value={contextValue}>
      {children}
      {uploadModalOpen && uploadTargetProjectId && (
        <ProjectUploadModalDialog
          streams={streams}
          hintStreamId={hintStreamId}
          uploadModalKind={uploadModalKind}
          onUploadModalKindChange={changeUploadModalKind}
          uploadRows={uploadRows}
          setUploadRows={setUploadRows}
          appendUploadFiles={appendUploadFiles}
          uploading={uploading}
          onCancel={closeModal}
          onSubmit={submitProjectUpload}
          proposalStructures={hubStructures}
          structuresReady={structuresReady}
        />
      )}
    </ProjectUploadModalContext.Provider>
  );
}
